import { useMemo } from "react";
import AsyncSelect from "react-select/async";
import api from "../../services/api";

// Google Places billing trick: when an Autocomplete and the Place Details
// that follows share a session token, the Details call is free. Reset the
// token after each Details call so the next session starts fresh.
let activeSessionToken = null;
function getSessionToken() {
  if (!activeSessionToken) {
    activeSessionToken = `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  }
  return activeSessionToken;
}
function clearSessionToken() {
  activeSessionToken = null;
}

export async function geocodeByPlaceId(placeId) {
  const { data } = await api.get("/places/details", { params: { place_id: placeId } });
  clearSessionToken();
  const r = data.result || {};
  return [{
    place_id: r.place_id,
    formatted_address: r.formatted_address,
    address_components: r.address_components || [],
    geometry: r.geometry || { location: { lat: null, lng: null } },
    name: r.name,
    // Newly surfaced by server PR — admin Add Venue prefills these so
    // the address picker fills name / phone / website / about in one
    // shot instead of the admin typing them by hand.
    formatted_phone_number: r.formatted_phone_number || null,
    international_phone_number: r.international_phone_number || null,
    website: r.website || null,
    editorial_summary: r.editorial_summary || null,
  }];
}

function debouncePromise(fn, delay) {
  let timer = null;
  return (...args) => new Promise((resolve, reject) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      Promise.resolve(fn(...args)).then(resolve, reject);
    }, delay);
  });
}

export default function PlacesAutocomplete({
  selectProps = {},
  autocompletionRequest = {},
  debounce = 300,
}) {
  const types = autocompletionRequest && autocompletionRequest.types && autocompletionRequest.types[0];

  const loadOptions = useMemo(() => debouncePromise(async (input) => {
    if (!input || input.length < 2) return [];
    const params = { input, sessiontoken: getSessionToken() };
    if (types) params.types = types;
    try {
      const { data } = await api.get("/places/autocomplete", { params });
      return (data.predictions || []).map((p) => ({
        label: p.description,
        value: {
          place_id: p.place_id,
          description: p.description,
          structured_formatting: p.structured_formatting,
          terms: p.terms,
          types: p.types,
        },
      }));
    } catch (err) {
      console.error("places autocomplete failed:", err);
      return [];
    }
  }, debounce), [types, debounce]);

  return (
    <AsyncSelect
      cacheOptions={false}
      defaultOptions={false}
      loadOptions={loadOptions}
      {...selectProps}
    />
  );
}
